import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';
import UsabillaInPageComponent from '@/sharedcomponents/pageComponents/usabillaInPageComponent/UsabillaInPageComponent.vue';
import { HouseSettings } from './HouseSettings';
import { HouseModel, STEPS } from './HouseModel';
import HouseCalculator from './HouseCalculator';
import ChoosePackageStepComponent from '../commonSteps/ChoosePackageStepComponent.vue';
import ContactInformationStepComponent from '../commonSteps/ContactInformationStepComponent.vue';
import OverviewStepComponent from '../commonSteps/OverviewStepComponent.vue';
import AdditionalInfoStepComponent from '../commonSteps/AdditionalInfoStepComponent.vue';
import PersonInfoStepComponent from './steps/PersonInfoStepComponent.vue';
import PaymentStepComponent from '../commonSteps/PaymentStepComponent.vue';
import ReceiptStepComponent from '../commonSteps/ReceiptStepComponent.vue';
import CampaignStepComponent from '../commonSteps/CampaignStepComponent.vue';
import { PropType } from 'vue';
import { CardBlock, HtmlBlock, UsabillaInPageBlock } from '@/cms/definitions/content-types';
import store from '@/store/store';
import { CALCULATOR_LOADED, INIT_CALCULATOR, RESET_CALCULATORS } from '@/store/modules/calculatorContext';
import { gotoAddInsurances, nextStep, setFirstCard } from '../services/NavigationService';
import { resetSelectedCalculation, subscribeBasketChange } from '../services/CalculationService';
import {
	getShowReceipt,
	handleValidCustomerAge,
	isValidAdditionalInfo,
	isValidAlmbrandProducts,
	isValidContactInfo,
	isValidPackage,
	isValidPaymentInfo,
	showValidNowOption,
	Validator,
} from '../services/ValidationService';
import {
	getCardTitle,
	getSubtitle,
	setDetailsYearlyPrice,
	setGlobalPackageSums,
	setHighLights,
} from '../services/ProductService';
import { initComponent, STEP } from '../services/UiStepService';
import MapOrderDescription from './MapOrderDescription';
import { CalculatorUiComponent } from '../BuyInsuranceComponent';
import InsurancesProductEnum from '@/enums/InsurancesProductEnum';

@Options({
	name: 'BuyInsuranceHouseComponent',
	props: {
		calculatorInfoBlock: Object as PropType<Array<HtmlBlock>>,
		contentBlocks: Object as PropType<Array<CardBlock>>,
		cardReceipt: Object as PropType<CardBlock>,
		settingsBlock: Object as PropType<HtmlBlock>,
		usabillaBlock: Object as PropType<UsabillaInPageBlock>,
		contentUrl: String, // Used by service to handle browser history
		headerBadgeTxt: String,
		hasBadgeText: Boolean,
		productName: String,
	},
	components: {
		UsabillaInPageComponent,
		PersonInfoStepComponent,
		ChoosePackageStepComponent,
		OverviewStepComponent,
		AdditionalInfoStepComponent,
		ContactInformationStepComponent,
		PaymentStepComponent,
		ReceiptStepComponent,
		CampaignStepComponent,
	},
	computed: mapState<any>({
		model: (state) => state.calculatorContext[state.calculatorContext.active].model,
		cms: (state) => state.calculatorContext[state.calculatorContext.active].cms,
	}),
})
export default class BuyHouse extends Vue implements CalculatorUiComponent {
	contentBlocks: Array<CardBlock>;
	cardReceipt: CardBlock;
	settingsBlock: HtmlBlock;
	usabillaBlock: UsabillaInPageBlock;
	contentUrl: string; // Used by service to handle browser history
	headerBadgeTxt: string;
	hasBadgeText: boolean;
	productName: string; // Either husforsikring or fritidshusforsikring
	calculatorInfoBlock: HtmlBlock;

	// list and order of steps
	public steps: Array<string> = STEPS;

	public model!: HouseModel; // model from store
	public cms!: HouseSettings; // setting from store
	public calculator: HouseCalculator = null;
	public componentInit = false;
	public checkout = false;
	private mapOrderDescription: MapOrderDescription;
	private unsubScribeBasketChange: Function;

	public created() {
		this.initCreated();
	}

	private async initCreated() {
		// this component is used for holidayhouse and house
		if (store.getters.getActiveCalculator) {
			await store.dispatch(CALCULATOR_LOADED);
		} else {
			// this is first load - INIT store
			const cms = new HouseSettings(this.settingsBlock);
			const model: HouseModel = new HouseModel();
			model.productName = this.productName;

			if (cms.mockData) {
				HouseModel.mock(model);
			}
			await store.dispatch(INIT_CALCULATOR, {
				cms,
				model,
			});
		}

		if (!(await initComponent(this))) {
			return;
		}

		this.calculator = new HouseCalculator(this);

		setFirstCard(this);
	}
	public mounted(): void {
		this.unsubScribeBasketChange = subscribeBasketChange(this);
	}

	beforeUnmount() {
		if (this.unsubScribeBasketChange) {
			this.unsubScribeBasketChange();
		}
	}

	// called by service
	public addResetSubscription() {
		const model = new HouseModel();
		model.productName = this.productName;
		const unsubscribe = store.subscribeAction((action, state) => {
			if (action.type === RESET_CALCULATORS) {
				setTimeout(() => {
					this.checkout = true;
					store.dispatch(INIT_CALCULATOR, {
						cms: this.cms,
						model,
					});
					unsubscribe();
				}, 2000);
			}
		});
	}

	public get showValidNowOption() {
		return showValidNowOption(this.model);
	}

	public async gotoCard(cardName: string): Promise<boolean> {
		if (cardName === STEP.PERSON_INFO || cardName === STEP.PACKAGE) {
			resetSelectedCalculation(this.model);
		}

		if (cardName === STEP.PACKAGE) {
			// check for invalid address
			const ok = await this.calculator.checkForValidAddress();
			if (!ok) {
				this.model.currentCardName = cardName;
				this.gotoCard(STEP.PERSON_INFO);
				return false;
			}
			this.calculator.setupExcessList();

			// check Age:
			if (!handleValidCustomerAge(this.model, this.cms)) {
				this.gotoCard(STEP.PERSON_INFO);
				return false;
			}
		}

		if (cardName === STEP.PAYMENT) {
			// force user to choose yearly/monthly payment to ensure progressive steps
			this.model.choosePackage.monthYear = undefined;
			this.model.payment.userSelected = false;
		}

		this.model.currentCardName = cardName;

		if (cardName === STEP.ORDER) {
			store.state.showSpinner = true;
			if (!this.isValid(this.model.currentCardName)) {
				store.state.showSpinner = false;
				return false;
			}
			this.mapOrderDescription = new MapOrderDescription(this);
			await this.mapOrderDescription.orderByEmail();
			return false;
		}
		return true;
	}

	/**
	 * when nextbtn is clicked - check for valid
	 * @param cardName
	 */
	public nextStep(cardName: string, addToHistory = true) {
		nextStep(cardName, addToHistory, this);
	}

	public getSubtitle(cardName: string) {
		return getSubtitle(cardName, this.model);
	}

	public isActiveCard(cardName: string): boolean {
		return cardName === this.model.currentCardName;
	}

	/* eslint-disable */
	public isValid(cardName): boolean {
		switch (cardName) {
			case STEP.PERSON_INFO:
				let okPerson = false;
				if (this.model.productName === InsurancesProductEnum.FRITIDSHUS_FORSIKRING) {
					okPerson = this.model.personInfo.holidayAddress.address !== undefined;
				} else {
					okPerson = this.model.personInfo.address !== undefined;
				}
				if (!okPerson) {
					return false;
				}
				okPerson =
					Validator.isValidAge(this.model.personInfo.customerAge) && isValidAlmbrandProducts(this.model);
				if (okPerson) {
					this.model.personInfo.customerAge = this.model.personInfo.customerAge.trim().replace(/\D/g, '');
					let address = this.model.personInfo.address;
					if (this.model.productName === InsurancesProductEnum.FRITIDSHUS_FORSIKRING) {
						address = this.model.personInfo.holidayAddress.address;
					}
					this.model.personInfo.subtitle = `${this.model.personInfo.customerAge} år, ${address}`;
				}
				return okPerson;
			case STEP.PACKAGE:
				return isValidPackage(this);
			case STEP.OVERVIEW:
				return isValidPackage(this);
			case STEP.CONTACT_INFORMATION:
				return isValidContactInfo(this.model, this.cms);
			case STEP.ADDITIONAL_INFO:
				return isValidAdditionalInfo(false, this.model, this.cms);
			case STEP.PAYMENT:
				return isValidPaymentInfo(this.model, this.cms);
			default:
				return true;
		}
	}
	/* eslint-enable */

	// called by service
	public setOverviewData() {
		setHighLights(this.model);
		let address;
		if (this.model.productName === InsurancesProductEnum.FRITIDSHUS_FORSIKRING) {
			address = this.model.personInfo.holidayAddress.address;
		} else {
			address = this.model.personInfo.address;
		}

		this.model.overview.details = [
			this.model.personInfo.customerAge + ' år',
			address,
			this.model.personInfo.familyMemberCount,
			'Selvrisiko: ' + this.getExcessAmount(),
		];
		setGlobalPackageSums(this.model);
		setDetailsYearlyPrice(this.model, this.cms);
	}

	/** called by service */
	public buildDescriptionForEmail(desc: string) {
		return this.mapOrderDescription.buildDescriptionForEmail(desc);
	}

	public getExcessAmount(): string {
		const inx = this.model.ownRiskOptions.indexOf(this.model.choosePackage.ownRiskId);
		return this.model.ownRiskLabels[inx];
	}

	public gotoAddInsurances(step: string) {
		gotoAddInsurances(step, this);
	}

	public get showReceipt() {
		return getShowReceipt(this);
	}

	public getCardTitle(card): string {
		return getCardTitle(card, this.model, this.cms);
	}
}
